import { Button } from '@/components/ui/button'
import { NodeListItem, type NodeItem, type NodeContext, type NodeConfig } from './NodeListItem'
import { ClipboardList, CheckSquare, MessageSquare, FileText, Building } from 'lucide-react'
import type { Message } from '@/types'

// Re-export types from NodeListItem for convenience
export type { NodeItem, NodeContext, NodeConfig } from './NodeListItem'

// NodeList-specific configuration interface that includes all context-specific options plus NodeList-specific options
export interface NodeListConfig {
  // Common base options
  className?: string
  spacing?: 'xs' | 'sm' | 'md' | 'lg'
  textSize?: 'xs' | 'sm' | 'base'
  hoverMode?: boolean

  // Project-specific options
  showStatus?: boolean
  showPriority?: boolean
  showHorizon?: boolean
  showWorkspace?: boolean
  showUserCount?: boolean
  showDescription?: boolean
  showCreator?: boolean
  showDate?: boolean
  avatarSize?: 'xs' | 'sm' | 'md' | 'lg'
  avatarPosition?: 'left' | 'center' | 'right' | 'bottom'

  // Task-specific options
  showType?: boolean
  showProject?: boolean
  showAssignee?: boolean
  showDueDate?: boolean
  showSubtasks?: boolean
  statusSize?: 'sm' | 'md'
  assigneePosition?: 'left' | 'center' | 'right' | 'bottom'
  dueDatePosition?: 'left' | 'center' | 'right' | 'bottom'

  // Message-specific options
  showTitle?: boolean
  maxLines?: number
  showTasks?: boolean
  showDocuments?: boolean
  showProjects?: boolean
  showReplyCount?: boolean
  associationsMaxVisible?: number
  associationsDisplayMode?: 'badge' | 'text' | 'card'
  showRelationshipCounts?: boolean
  showReadStatus?: boolean
  showMessageType?: boolean
  currentUserId?: string
  onClick?: (message: Message) => void

  // Document-specific options
  showTypeIcon?: boolean
  showCreatorName?: boolean
  showSharedCount?: boolean
  typeIconPosition?: 'left' | 'center' | 'right' | 'bottom'
  creatorPosition?: 'left' | 'center' | 'right' | 'bottom'

  // NodeList-specific options
  maxVisible?: number
  emptyMessage?: string
  emptyState?: string
  itemClassName?: string
  onShowMore?: () => void
}

// Main props interface for NodeList
export interface NodeListProps<T extends NodeItem = NodeItem> {
  /**
   * Array of nodes to display
   */
  items: T[]
  /**
   * Context type - determines which ListItem component and configuration to use
   */
  context: NodeContext
  /**
   * Configuration object for the specific context (uses unified system from NodeListItem)
   */
  config?: NodeListConfig
  /**
   * Custom className for the container
   */
  className?: string
}

// Empty state icons for each context
const EMPTY_STATE_ICONS = {
  project: ClipboardList,
  task: CheckSquare,
  message: MessageSquare,
  document: FileText,
  workspace: Building,
} as const

/**
 * Unified list component that can display any type of node (project, task, message, document)
 * with context-specific configuration and rendering using the standardized system from NodeListItem.
 */
export function NodeList<T extends NodeItem>({
  items,
  context,
  config = {},
  className = '',
}: NodeListProps<T>) {
  // Get the appropriate empty state icon
  const EmptyIcon = EMPTY_STATE_ICONS[context]

  // Handle empty state
  if (items.length === 0) {
    return config.emptyState ? (
      <div className={`text-center py-8 text-muted-foreground ${className}`}>
        <EmptyIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p className="text-sm">{config.emptyState}</p>
      </div>
    ) : (
      <div className={`text-center py-8 text-muted-foreground ${className}`}>
        <EmptyIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p className="text-sm">{config.emptyMessage || `No ${context}s`}</p>
      </div>
    )
  }

  // Handle maxVisible logic
  const visibleItems = config.maxVisible ? items.slice(0, config.maxVisible) : items
  const remainingCount =
    config.maxVisible && items.length > config.maxVisible ? items.length - config.maxVisible : 0

  // Extract NodeListItem config (exclude NodeList-specific options)
  const {
    maxVisible: _maxVisible,
    emptyMessage: _emptyMessage,
    emptyState: _emptyState,
    itemClassName: _itemClassName,
    onShowMore: _onShowMore,
    ...nodeListItemConfig
  } = config

  return (
    <div className={`flex flex-col gap-3 ${className}`}>
      {visibleItems.map((item) => (
        <NodeListItem
          key={item.id}
          item={item}
          context={context}
          config={nodeListItemConfig as NodeConfig}
        />
      ))}
      {remainingCount > 0 && (
        <div className="flex items-center justify-center pt-2">
          {_onShowMore && (
            <Button
              variant="link"
              size="sm"
              onClick={_onShowMore}
              className="h-auto p-0 ml-2 text-sm"
            >
              Show {remainingCount} more
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
